import * as Sentry from '@sentry/react'

import { selectScreenName } from 'root-redux/selects/common'
import {
  selectUserPaymentMethod,
  selectUserPaymentSystem,
} from 'root-redux/selects/user'
import { store } from 'root-redux/store'

import {
  FUNNEL_ERROR_TITLES,
  FunnelErrorCode,
  TFunnelErrorCode,
  TFunnelErrorTag,
} from 'helpers/funnelErrors'

const TRACKED_FUNNEL_ERROR_CODES: TFunnelErrorCode[] = [
  FunnelErrorCode.FUNNEL_UUID_CREATION_FAILED,
  FunnelErrorCode.FUNNEL_VARIANT_LOAD_FAILED,
  FunnelErrorCode.FUNNEL_APP_CONFIG_LOAD_FAILED,
  FunnelErrorCode.FUNNEL_PAYMENT_CONFIG_LOAD_FAILED,
  FunnelErrorCode.FUNNEL_GROWTHBOOK_INIT_FAILED,
  FunnelErrorCode.FUNNEL_APP_READY_TIMEOUT,
  FunnelErrorCode.FUNNEL_STEPS_EMPTY_TIMEOUT,
  FunnelErrorCode.PRIMER_SDK_INIT_FAILED,
  FunnelErrorCode.PRIMER_SESSION_TOKEN_FAILED,
  FunnelErrorCode.PRIMER_SESSION_UPDATE_FAILED,
  FunnelErrorCode.FUNNEL_SUBSCRIPTION_LIST_EMPTY,
  FunnelErrorCode.FUNNEL_LANGUAGE_LOAD_FAILED,
  FunnelErrorCode.STRIPE_WALLET_AVAILABILITY_CHECK_FAILED,
]

type TSeverityLevel = 'fatal' | 'error' | 'warning'

type TCaptureOptions = {
  level?: TSeverityLevel
  tags?: Partial<Record<TFunnelErrorTag, string>>
  extra?: Record<string, unknown>
  groupBy?: string[]
}

const toError = (message: string, cause: unknown): Error => {
  if (cause instanceof Error) return new Error(message, { cause })
  if (cause === null || cause === undefined) return new Error(message)

  return new Error(`${message}: ${String(cause)}`)
}

export const captureFunnelError = (
  code: TFunnelErrorCode,
  cause: unknown,
  { level = 'error', tags, extra, groupBy }: TCaptureOptions = {},
): void => {
  if (!TRACKED_FUNNEL_ERROR_CODES.includes(code)) return
  if (!Sentry.isInitialized()) return

  try {
    const fingerprint = groupBy ? [code, ...groupBy] : [code]

    const state = store.getState()

    Sentry.captureException(toError(FUNNEL_ERROR_TITLES[code], cause), {
      level,
      tags: {
        error_code: code,
        screen_name: selectScreenName(state),
        payment_method: selectUserPaymentMethod(state),
        payment_system: selectUserPaymentSystem(state),
        ...tags,
      },
      contexts: { funnel: { ...extra } },
      fingerprint,
    })
  } catch (loggingError) {
    console.warn('captureFunnelError failed', loggingError)
  }
}
